import { apiClient } from '../../apiClient';
import realtimeService from '../../realtimeService';

/**
 * Core LLM Service
 * Handles project-agnostic LLM interactions like basic querying and feedback.
 * Also implements IDataAgentLLMService interface from shared library.
 */
export class LLMCoreService {
    constructor(baseUrl = '/llm') {
        this.BASE_URL = baseUrl;
    }

    /**
     * Send a query via the Smart Query Path (two-pass planner + explicit routing).
     * Returns the same shape as queryLLM plus a smart_query_metadata field.
     *
     * @param {Object} options
     * @param {string} options.query
     * @param {string} options.sessionId
     * @param {Object} [options.context]
     * @param {AbortSignal} [options.signal]
     * @returns {Promise<Object>}
     */
    async querySmartPath(options) {
        const payload = {
            query: options.query,
            sessionId: options.sessionId,
            context: {
                ...(options.context || {}),
                mentions: Array.isArray(options.mentions)
                    ? options.mentions
                    : (options.context?.mentions || undefined),
            },
        };
        const requestConfig = options.signal ? { signal: options.signal } : {};
        const response = await apiClient.post(`${this.BASE_URL}/smart-query`, payload, requestConfig);

        const data =
            response?.response !== undefined || response?.trace_id !== undefined
                ? response
                : response?.data ?? response;

        const topNeedsClarification = Boolean(data?.needsClarification);
        const innerNeedsClarification = Boolean(data?.data?.needsClarification);
        const finalNeedsClarification = topNeedsClarification || innerNeedsClarification || (data?.status === 'clarification') || (data?.data?.status === 'clarification') || (data?.data?.routerDecision?.action === 'clarify');

        let ambiguities = Array.isArray(data?.ambiguities) ? data.ambiguities : [];
        if (ambiguities.length === 0 && Array.isArray(data?.data?.ambiguities)) {
            ambiguities = data.data.ambiguities;
        }

        if (ambiguities.length === 0) {
            const routerDecision = data?.data?.routerDecision || data?.routerDecision;
            const candidateMetrics = routerDecision?.candidateMetrics;
            if (Array.isArray(candidateMetrics) && candidateMetrics.length > 0) {
                ambiguities = [{
                    id: 'metric-resolution',
                    message: routerDecision.clarification || data.response || 'Please select one of the following options:',
                    options: candidateMetrics.map(m => ({
                        label: m.title || m.metric_name,
                        value: m.metric_name,
                        entityName: m.title || m.metric_name,
                        metric_name: m.metric_name
                    }))
                }];
            }
        }

        return {
            response: data.response || data.answer || data.message || '',
            sources: data.sources || [],
            visualizations: data.visualizations || [],
            data: data.data || null,
            needsClarification: finalNeedsClarification,
            clarificationType: data?.clarificationType || data?.data?.clarificationType || null,
            ambiguities,
            correlationId: data.correlationId || null,
            traceId: data.trace_id || null,
            durationMs: data.durationMs || null,
            smartQueryMetadata: data.smart_query_metadata || null,
            status: data.status || 'ok',
        };
    }

    /**
     * Send a query to the LLM engine
     * @param {Object} options
     * @param {string} options.query - The user's message
     * @param {Object} [options.context] - Additional context
     * @param {string} [options.systemPrompt] - Override system prompt
     * @param {string} [options.sessionId] - Session identifier
     * @param {AbortSignal} [options.signal] - Abort signal for cancellation
     * @returns {Promise<Object>} The LLM response
     */
    async queryLLM(options) {
        if (options?.context?.useSmartQueryPath || options?.context?.useSmartPath) {
            return this.querySmartPath(options);
        }

        const payload = {
            query: options.query,
            context: {
                ...(options.context || {}),
                mentions: Array.isArray(options.mentions) ? options.mentions : (options.context?.mentions || undefined),
            },
            systemPrompt: options.systemPrompt,
            sessionId: options.sessionId,
        };
        const requestConfig = options.signal ? { signal: options.signal } : {};
        const response = await apiClient.post(`${this.BASE_URL}/query`, payload, requestConfig);

        // Normalize response for shared library compatibility
        const data =
            response?.response !== undefined ||
            response?.planId !== undefined ||
            response?.correlationId !== undefined
                ? response
                : (response?.data?.response !== undefined ||
                   response?.data?.planId !== undefined ||
                   response?.data?.correlationId !== undefined)
                    ? response.data
                    : (response?.payload || response);

        const topNeedsClarification = Boolean(data?.needsClarification);
        const innerNeedsClarification = Boolean(data?.data?.needsClarification);
        const finalNeedsClarification = topNeedsClarification || innerNeedsClarification || (data?.status === 'clarification') || (data?.data?.status === 'clarification') || (data?.data?.routerDecision?.action === 'clarify');

        let ambiguities = Array.isArray(data?.ambiguities) ? data.ambiguities : [];
        if (ambiguities.length === 0 && Array.isArray(data?.data?.ambiguities)) {
            ambiguities = data.data.ambiguities;
        }

        if (ambiguities.length === 0) {
            const routerDecision = data?.data?.routerDecision || data?.routerDecision;
            const candidateMetrics = routerDecision?.candidateMetrics;
            if (Array.isArray(candidateMetrics) && candidateMetrics.length > 0) {
                ambiguities = [{
                    id: 'metric-resolution',
                    message: routerDecision.clarification || data.response || 'Please select one of the following options:',
                    options: candidateMetrics.map(m => ({
                        label: m.title || m.metric_name,
                        value: m.metric_name,
                        entityName: m.title || m.metric_name,
                        metric_name: m.metric_name
                    }))
                }];
            }
        }

        return {
            response: data.response || data.answer || data.message || '',
            sources: data.sources || [],
            visualizations: data.visualizations || [],
            planId: data.planId,
            data: data.data || null,
            needsClarification: finalNeedsClarification,
            clarificationType: data?.clarificationType || data?.data?.clarificationType || null,
            ambiguities,
            correlationId: data.correlationId || null,
            durationMs: data.durationMs || null
        };
    }

    /**
     * Submit user feedback for a response
     * @param {Object} payload
     * @param {string} payload.messageId - ID of the message
     * @param {string} payload.rating - 'positive' or 'negative'
     * @param {string} [payload.comment] - Optional comment
     * @returns {Promise<void>}
     */
    async submitFeedback(payload) {
        await apiClient.post(`${this.BASE_URL}/feedback`, payload);
    }

    /**
     * Get the current progress for a session
     * @param {Object} options
     * @param {string} options.sessionId
     * @returns {Promise<Object>}
     */
    async getProgress(options) {
        const response = await apiClient.get(`${this.BASE_URL}/progress/${encodeURIComponent(options.sessionId)}`);
        return response.data || response;
    }

    /**
     * Subscribe to real-time progress updates via Firestore
     * @param {Object} options
     * @param {string} options.sessionId
     * @param {Function} options.onUpdate
     * @param {Function} [options.onError]
     * @returns {Function} Unsubscribe function
     */
    subscribeToProgress(options) {
        const { sessionId, onUpdate, onError } = options;
        const listenerKey = `llm-progress-${sessionId}`;
        
        return realtimeService.listenToDocument(
            listenerKey,
            'llm_query_progress',
            sessionId,
            (data) => {
                if (data) {
                    onUpdate(data);
                } else {
                    onUpdate(null);
                }
            },
            onError
        );
    }

    async searchMentions(options) {
        const query = encodeURIComponent(String(options?.query || '').trim());
        const types = Array.isArray(options?.types) && options.types.length > 0
            ? `&types=${encodeURIComponent(options.types.join(','))}`
            : '';
        const limit = Number(options?.limit) > 0 ? `&limit=${Number(options.limit)}` : '';
        const response = await apiClient.get(`${this.BASE_URL}/mentions?q=${query}${types}${limit}`);
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        return [];
    }
}

// Create adapter instance for shared library
export const llmCoreServiceAdapter = new LLMCoreService();
